import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Minus, AlertTriangle, Lock } from 'lucide-react';
import { numberLocale } from '@/lib/language';
import { trackConversion } from '@/lib/analytics';
import { maskCustomerId } from '@/lib/maskCustomerId';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { JourneyStep, JourneyStepDetail, JourneyStepId, JourneyStepState } from '@/lib/journey';
import { chipClickFor } from '@/lib/journeyTargets';

/**
 * Die sechs Chips der Fortschrittsleiter (Umbau Statusfluss, 16.09.2026,
 * Spec `docs/superpowers/specs/2026-09-16-journeybar-statusfluss-design.md`
 * Abschnitt 3/4). Loest die vier Chips der Vorgaengerfassung ab (T3 "Vier
 * Punkte, ein Wort, eine Zahl") — die Leiter traegt seitdem zwei weitere
 * Schritte (Google-Ads-Konto verbinden, Segmente als Kampagnen), und
 * `JourneyStep` traegt seit dem Umbau kein `secondary`/`rejected` mehr,
 * sondern ein typisiertes `detail` je Schrittart (`JourneyStepDetail` in
 * `lib/journey.ts`).
 *
 * Regel 2 der Spec ("Zahl = Liste"): eine Zahl am Chip ist GENAU die Menge,
 * die der Klick oeffnet. Die Schritte 2 (Optimieren) und 3 (Freigabe) tragen
 * deshalb eine Zahl — nur wenn sie > 0 ist, sonst ein Zustandswort, denn ein
 * Chip "0" oeffnete eine leere Liste. Die Schritte 1, 4, 5 und 6 (Feed, Live,
 * Anzeigen, Kampagnen) sind ZUSTANDSCHIPS: sie zeigen nie eine Zahl, nur ein
 * Wort ("gesynct vor 3 Tagen", "live", "verbunden", "demnaechst"). Ihre
 * Zahlen stehen ausschliesslich im Tooltip (`detail`) und, wo es dazu eine
 * Liste gibt, im jeweiligen Kapitel des Feed-Checks.
 *
 * ZWEI NAMENSRAEUME, unveraendert aus der Vorgaengerfassung (Ledger-
 * Entscheidung D1): die kurzen Chip-Woerter (`label`, die Zustandswoerter je
 * Schritt) liegen in `dashboard:journey.*`. Die ausfuehrlichen Tooltip-Texte
 * liegen in `common:journey.*`. Drei bestehende Copy-Waechter
 * (`src/locales/journeyApproveCopy.test.ts`, `journeyApproveZero.test.ts`,
 * `productCountPlurals.test.ts`) zeigen auf die Freigabe-/Feed-Schluessel in
 * `common:journey.*` — die bleiben deshalb unveraendert stehen. Die
 * Wiederverwendung von `common:journey.approve.count`/`count_zero` als
 * Freigabe-Tooltip unten ist beabsichtigt, keine Kopie: dieselbe Aussage
 * ("N bereit zum Hochladen") gilt fuer den Chip-Bestand wie fuer den
 * Tooltip.
 *
 * `StepMark`/`MARK_CLASS`/`LABEL_CLASS`, die Zeiger-Logik (`pointerIndex`)
 * und der `TooltipProvider` sind aus der Vorgaengerfassung uebernommen.
 * `pointerIndex` faellt seit dem Umbau statt auf den LETZTEN Schritt der
 * Liste (`steps.length - 1`) auf den letzten ERLEDIGTEN Schritt zurueck:
 * Schritt 6 (Kampagnen) traegt den neuen Zustand `'upcoming'` und ist nie
 * `current`/`done`; ein Fallback auf das Listenende haette ihm faelschlich
 * `aria-current="step"` gegeben, obwohl an ihm nichts zu tun ist.
 *
 * `TooltipProvider` wird hier lokal gesetzt (Muster `JobHealthIndicator.tsx`):
 * `src/test/setup.ts` initialisiert keinen globalen Provider fuer
 * Komponententests, und verschachtelte Radix-Provider sind unproblematisch
 * (der globale aus `App.tsx` bleibt fuer die echte App bestehen).
 */
export interface JourneyChipsProps {
  steps: JourneyStep[];
  nextStep: JourneyStepId | null;
  onStepClick: (id: JourneyStepId) => void;
  /**
   * Nur fuer die Analytics-Zeile (`journey_step_click.target`): der Klick
   * selbst navigiert weiterhin ueber `onStepClick`. `ContextJourneyBand`
   * reicht dafuer dieselbe `journey.targets`-Map durch, aus der es auch sein
   * `navigate(...)` speist — eine zweite Quelle fuer dasselbe Ziel waere eine
   * Drift-Quelle.
   */
  targets: Record<JourneyStepId, string>;
  /** Default 'full'. `ContextJourneyBand` reicht 'micro' durch, sobald der Slot ueberlaeuft. */
  variant?: 'full' | 'micro';
  /**
   * Aktuelle Route, nur fuer das Analytics-Ziel (UX-Audit A3-09): der Klick
   * fuehrt laut `chipClickFor` nicht immer auf `targets[id]`, und
   * `journey_step_click.target` soll das tatsaechliche Ziel melden.
   */
  pathname?: string;
}

/** Wortgleich aus der Vorgaengerfassung: `data-icon` ist Teil des Test-
 *  Vertrags — ein uebersprungener Schritt darf NIE den Haken eines
 *  erledigten tragen. */
function StepMark({ state }: { state: JourneyStepState }) {
  switch (state) {
    case 'done':
      return <Check data-icon="check" className="h-3 w-3" strokeWidth={3} aria-hidden="true" />;
    case 'skipped':
      return <Minus data-icon="minus" className="h-3 w-3" strokeWidth={3} aria-hidden="true" />;
    case 'error':
      return <AlertTriangle data-icon="alert" className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />;
    case 'blocked':
      return <Lock data-icon="lock" className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden="true" />;
    case 'current':
      return <span data-icon="dot" className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />;
    case 'upcoming':
      // Schritt 6 (Kampagnen): ein gestrichelter Punkt statt des leeren
      // Kreises von 'pending' — kein "als naechstes dran", sondern ein
      // Ausblick ohne erreichbaren Zeiger (Spec 3.1).
      return (
        <span
          data-icon="dot-dashed"
          className="h-1.5 w-1.5 rounded-full border border-dashed border-current"
          aria-hidden="true"
        />
      );
    default:
      return <span data-icon="dot-empty" className="h-1.5 w-1.5 rounded-full border border-current" aria-hidden="true" />;
  }
}

const MARK_CLASS: Record<JourneyStepState, string> = {
  done: 'bg-success/10 text-success-ink',
  current: 'bg-primary/10 text-primary ring-2 ring-primary/20',
  pending: 'text-muted-foreground border border-border',
  skipped: 'bg-muted text-muted-foreground',
  error: 'bg-destructive/10 text-destructive-ink',
  blocked: 'bg-warning/10 text-warning-ink',
  // Kein Hintergrund: Schritt 6 ist ein Ausblick, kein erreichter Zustand —
  // der gestrichelte Ring markiert das, statt ihn wie 'done' einzufaerben.
  upcoming: 'border border-dashed border-muted-foreground/60 text-muted-foreground',
};

const LABEL_CLASS: Record<JourneyStepState, string> = {
  done: 'text-foreground',
  current: 'text-primary',
  pending: 'text-muted-foreground',
  skipped: 'text-muted-foreground',
  error: 'text-destructive-ink',
  blocked: 'text-warning-ink',
  upcoming: 'text-muted-foreground',
};

/**
 * Tage seit einem ISO-Zeitstempel, nie negativ (eine Uhrenabweichung waere
 * sonst "-1 Tage"). Reine Funktion, kein Hook — `Date.now()` wird bei jedem
 * Render neu gelesen, was fuer eine Chip-Anzeige reicht (keine Live-Uhr
 * noetig, ein Re-Render kommt ohnehin bei jeder Datenaenderung).
 */
function daysSince(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

export function JourneyChips({ steps, nextStep, onStepClick, targets, variant = 'full', pathname = '' }: JourneyChipsProps) {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const locale = numberLocale(i18n?.language);
  const fmt = (n: number) => n.toLocaleString(locale);

  // Zeiger-Logik: laufender oder blockierter Schritt zuerst, sonst der
  // fehlerhafte, sonst der LETZTE ERLEDIGTE — nicht mehr der letzte Schritt
  // der Liste (siehe Kopfkommentar). "Alles erledigt inklusive Ads-
  // Anschluss" zeigt damit auf Schritt 5 (ads_connect), nicht auf den
  // Kampagnen-Ausblick, der niemals 'done' wird.
  const pointerIndex = (() => {
    const active = steps.findIndex((s) => s.state === 'current' || s.state === 'blocked');
    if (active >= 0) return active;
    const failed = steps.findIndex((s) => s.state === 'error');
    if (failed >= 0) return failed;
    let lastDone = -1;
    steps.forEach((s, i) => {
      if (s.state === 'done') lastDone = i;
    });
    return lastDone >= 0 ? lastDone : 0;
  })();

  const handleClick = (step: JourneyStep) => {
    const click = chipClickFor(step, targets, pathname);
    trackConversion('journey_step_click', {
      step: step.id,
      is_next: step.id === nextStep ? 1 : 0,
      target: click.kind === 'navigate' ? click.to : 'feed-switcher',
    });
    onStepClick(step.id);
  };

  /**
   * Der sichtbare Kurztext (Spec 3/4, "Zahl = Liste"): die drei
   * Sonderzustaende zuerst (unveraendert aus der Vorgaengerfassung, ein Wort
   * je Zustand, unabhaengig vom Schritt), danach je Schrittart eine Zahl
   * (nur wenn sie > 0) oder ein Zustandswort.
   */
  const shortText = (step: JourneyStep): string => {
    // Mitglied: Schritt 5 liegt beim Eigentuemer, das ist kein Uebersprungen.
    if (step.detail.kind === 'ads_connect' && step.detail.ownerManaged) {
      return t('dashboard:journey.ads_connect.ownerManaged');
    }
    if (step.state === 'skipped') return t('dashboard:journey.state.skipped');
    if (step.state === 'error') return t('dashboard:journey.state.error');
    if (step.state === 'blocked') return t('dashboard:journey.state.blocked');

    const detail = step.detail;
    switch (detail.kind) {
      case 'connect': {
        // 'done' ohne Zeitstempel ist der Alt-Daten-Fall (Produkte
        // vorhanden, aber kein rueckwirkend befuellter `last_synced_at`) —
        // "nie gesynct" beschreibt hier den fehlenden Beleg, nicht den
        // wirklichen Zustand. 0 Produkte UND kein Zeitstempel trennt
        // `detail.hasMerchantCenter` (WP5 A2O-16): mit Merchant Center ist
        // der Feed verbunden, aber leer; ohne ist er nicht verbunden.
        if (detail.total === 0 && detail.lastSyncedAt === null) {
          return detail.hasMerchantCenter
            ? t('dashboard:journey.connect.empty')
            : t('dashboard:journey.connect.notConnected');
        }
        if (detail.lastSyncedAt === null) {
          return t('dashboard:journey.connect.neverSynced');
        }
        const days = daysSince(detail.lastSyncedAt);
        if (days === 0) return t('dashboard:journey.connect.syncedToday');
        return t('dashboard:journey.connect.syncedAgo', { count: days, formatted: fmt(days) });
      }
      case 'optimize':
        // Die sichtbare Zahl ist `step.count` (Plan Task 3, Punkt 2) — bei
        // 'optimize' identisch zu `detail.toReview` (journey.ts garantiert
        // das, siehe `countFor`/`detailFor`), aber `step.count` ist die
        // Groesse, die auch der Klick oeffnet ("Zahl = Liste").
        if (step.count > 0) {
          return t('dashboard:journey.optimize.toReview', { count: step.count, formatted: fmt(step.count) });
        }
        return detail.optimized === 0
          ? t('dashboard:journey.optimize.nothingYet')
          : t('dashboard:journey.optimize.reviewed');
      case 'approve':
        if (step.count > 0) {
          return t('dashboard:journey.approve.ready', { count: step.count, formatted: fmt(step.count) });
        }
        // WP5 K23: der Zeiger steht hier, weil Produkte WARTEN. "nichts offen"
        // widersprach der Handlungskarte ("N Produkte freigeben") direkt.
        if (step.state === 'current' && (detail.toApprove ?? 0) > 0) {
          return t('dashboard:journey.approve.upNext');
        }
        return t('dashboard:journey.approve.nothingOpen');
      case 'upload':
        if (detail.live === 0) return t('dashboard:journey.upload.nothingLive');
        if (detail.stale > 0) {
          return t('dashboard:journey.upload.stale', { count: detail.stale, formatted: fmt(detail.stale) });
        }
        return t('dashboard:journey.upload.live');
      case 'ads_connect':
        if (!detail.linked) {
          return detail.accountConnected
            ? t('dashboard:journey.ads_connect.notAssigned')
            : t('dashboard:journey.ads_connect.notConnected');
        }
        return detail.numbersReady
          ? t('dashboard:journey.ads_connect.connected')
          : t('dashboard:journey.ads_connect.numbersPending');
      case 'ads_campaigns':
        return t('dashboard:journey.ads_campaigns.upcoming');
      default:
        return '';
    }
  };

  /**
   * Der ausfuehrliche Tooltip-Inhalt (Spec 3, Spalte "Tooltip"): dieselben
   * drei Sonderzustaende wie oben, aber mit dem LANGEN Wort aus `common:`
   * (unveraendert aus der Vorgaengerfassung — "Sync fehlgeschlagen" statt
   * "Fehler"), danach die vollen Zahlen/Angaben je Schrittart.
   */
  const tooltipDetail = (step: JourneyStep): string => {
    if (step.detail.kind === 'ads_connect' && step.detail.ownerManaged) {
      return t('common:journey.ads_connect.ownerManaged');
    }
    if (step.state === 'skipped') return t('common:journey.skipped');
    if (step.state === 'error') return t('common:journey.error');
    if (step.state === 'blocked') return t('common:journey.blocked');

    const detail: JourneyStepDetail = step.detail;
    switch (detail.kind) {
      case 'connect':
        return t('common:journey.connect.count', { count: detail.total, formatted: fmt(detail.total) });
      case 'optimize':
        return t('common:journey.optimize.detail', {
          optimizedFormatted: fmt(detail.optimized),
          toReviewFormatted: fmt(detail.toReview),
        });
      case 'approve':
        if (detail.ready === 0 && (detail.toApprove ?? 0) > 0) {
          return t('common:journey.approve.waiting', {
            count: detail.toApprove,
            formatted: fmt(detail.toApprove ?? 0),
          });
        }
        return t('common:journey.approve.count', { count: detail.ready, formatted: fmt(detail.ready) });
      case 'upload': {
        const base = t('common:journey.upload.detail', {
          liveFormatted: fmt(detail.live),
          staleFormatted: fmt(detail.stale),
        });
        if (!detail.lastUploadAt) return base;
        const date = new Date(detail.lastUploadAt).toLocaleDateString(locale);
        return `${base} · ${t('common:journey.upload.lastUploadAt', { date })}`;
      }
      case 'ads_connect': {
        if (!detail.linked) {
          return detail.accountConnected
            ? t('dashboard:journey.ads_connect.notAssigned')
            : t('dashboard:journey.ads_connect.notConnected');
        }
        const parts: string[] = [];
        if (detail.customerId) {
          parts.push(t('common:journey.ads_connect.detail', { id: maskCustomerId(detail.customerId) }));
        }
        parts.push(
          t(detail.numbersReady ? 'common:journey.ads_connect.numbers' : 'common:journey.ads_connect.numbersPending'),
        );
        return parts.join(' · ');
      }
      case 'ads_campaigns':
        return t('common:journey.ads_campaigns.detail');
      default:
        return '';
    }
  };

  /** Tooltip-Inhalt UND `aria-label`-Quelle in einem: beide duerfen nie
   *  auseinanderlaufen, deshalb genau eine Funktion fuer beide. */
  const tooltipText = (step: JourneyStep) => `${t(`dashboard:journey.${step.id}.label`)}: ${tooltipDetail(step)}`;

  /**
   * Zugaenglicher Name je Punkt (WCAG 2.5.3 "Label in Name", unveraendert
   * aus der Vorgaengerfassung): der sichtbare Kurztext ist ein PRAEFIX des
   * Namens, das Label-Wort bleibt in der Mikro-Variante Teil des Namens,
   * auch wenn es dort nicht sichtbar auf der Flaeche steht.
   */
  const accessibleName = (step: JourneyStep, includeLabel: boolean): string => {
    const label = t(`dashboard:journey.${step.id}.label`);
    const visible = includeLabel ? `${label} ${shortText(step)}` : shortText(step);
    return includeLabel
      ? `${visible}: ${tooltipDetail(step)}`
      : `${visible}: ${label} · ${tooltipDetail(step)}`;
  };

  if (variant === 'micro') {
    return (
      <TooltipProvider>
        <nav aria-label={t('common:journey.aria')} className="flex items-center gap-1.5 min-w-0">
          {steps.map((step, i) => {
            const isPointer = i === pointerIndex;
            return (
              <Tooltip key={step.id}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => handleClick(step)}
                    data-state={step.state}
                    aria-current={isPointer ? 'step' : undefined}
                    // Mikro-Variante: der Zeiger zeigt Label + Kurztext, der Name
                    // beginnt damit (WCAG 2.5.3); die uebrigen Punkte nur ihre Marke.
                    aria-label={accessibleName(step, isPointer)}
                    className={`inline-flex items-center gap-1 shrink-0 text-xs tabular-nums whitespace-nowrap rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                      isPointer ? 'text-primary font-bold' : 'text-muted-foreground'
                    }`}
                  >
                    <span className={`h-4 w-4 shrink-0 rounded-full grid place-items-center ${MARK_CLASS[step.state]}`}>
                      <StepMark state={step.state} />
                    </span>
                    {/* NUR am Zeiger steht Text, und das ist gemessen, nicht
                        gespart (Spec 4.1, Platzbilanz vom 16.09.2026, sechs
                        Chips, Konto dennisgerich.sea@ auf dem Feed golfimport
                        CH-DE):

                          Fensterbreite   Band    Pille   Budget   voll braucht
                          1024 px          473 px  368 px    93 px       882 px
                          1280 px          729 px  368 px   349 px       882 px
                          1440 px          889 px  368 px   509 px       882 px
                          1920 px         1369 px  368 px   989 px       882 px

                        Die volle Fassung passt damit erst ab rund 1900 px.
                        Mit einem Kurztext an JEDEM der sechs Punkte brauchte
                        auch die Mikro-Fassung mehr als die 509 px bei 1440 px
                        -- gemessen fiel die Leiste dort auf `hidden`, also auf
                        GAR KEINE Leiter, und das unterhalb von 1920 px auf
                        jedem geprueften Fenster. Das waere schlechter als die
                        vier Punkte von vorher, und die Spec erlaubt `hidden`
                        ausdruecklich nur unter 1024 px.

                        Der Zeiger traegt seinen Text, die uebrigen fuenf nur
                        ihre Marke: die eine Aussage, die ein schmales Fenster
                        braucht, ist "wo stehe ich", und die restlichen Zahlen
                        stehen im Tooltip und im `aria-label` JEDES Punkts
                        (siehe `accessibleName`) -- sie gehen also nicht
                        verloren, sie stehen nur nicht mehr gleichzeitig da.
                        Seit A3-23 (UX-Audit 26.09.2026) traegt der Zeiger Label
                        UND Kurztext: bei 1440 px stand dort nur "verbunden"
                        ohne Bezug. Das Label-Wort kostet rund 60 bis 70 px
                        (Mikro 193 px + ~70 = ~263 px), Budget ab 1280 px 349 px. */}
                    {isPointer && <span>{`${t(`dashboard:journey.${step.id}.label`)} ${shortText(step)}`}</span>}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{tooltipText(step)}</TooltipContent>
              </Tooltip>
            );
          })}
        </nav>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      {/* `w-auto` statt `w-full`: als Flex-Item im umschliessenden Mess-Slot
          (`ContextJourneyBand`'s `journey-slot`) soll dieses `<nav>` seine
          eigene Inhaltsbreite tragen, nicht sich per Prozentbreite auf die
          (ggf. bereits geschrumpfte) Slot-Breite zwingen lassen — sonst
          haette die Fit-Messung im Elternteil (`scrollWidth` vs.
          `clientWidth` auf dem Slot) einen zirkulaeren Bezugspunkt. */}
      <nav aria-label={t('common:journey.aria')} className="w-auto min-w-0">
        {/* `flex-nowrap` statt `flex-wrap`: die Punkte sollen bei Platzmangel
            UEBERLAUFEN (und vom Elternteil auf die Mikro-Variante herabgestuft
            werden), nicht in eine zweite Zeile umbrechen. */}
        <ol className="flex items-center gap-1 flex-nowrap">
          {steps.map((step, index) => {
            const isPointer = index === pointerIndex;
            return (
              <Fragment key={step.id}>
                <li
                  data-state={step.state}
                  data-pointer={isPointer ? 'true' : undefined}
                  // Der Zeiger wird auch dann angesagt, wenn er auf einen
                  // Fehler oder eine Blockade zeigt.
                  aria-current={isPointer ? 'step' : undefined}
                  // `flex items-center` ist kein Layout-Wunsch, sondern der
                  // Schutz vor der Grundlinie: als blockiges `<li>` bildet die
                  // Zeile eine Zeilenbox, und der `inline-flex`-Knopf richtet
                  // sich an deren Grundlinie aus. Die Grundlinie des Knopfs
                  // ist die seines ERSTEN Kindes, der Markierung, und die haengt
                  // vom Inhalt ab: ein `<svg>` (done/skipped/error/blocked)
                  // liefert seine Unterkante, also die Unterkante der 16-px-
                  // Markierung; der 6-px-Punkt des aktuellen Schritts sitzt
                  // zentriert und liefert eine Grundlinie 5 px hoeher. Der
                  // aktuelle Schritt stand deshalb rund 5 px tiefer als seine
                  // Nachbarn (Befund Dennis 09.09.2026, Beutel-Shop, 1440 px;
                  // in einer Sonde mit dem gebauten CSS reproduziert: Knopf-
                  // Oberkante 14,7 gegen 19,5 px). Ein Flex-Container hat
                  // keine Zeilenbox, `items-center` zentriert die Hoehe.
                  className="flex items-center"
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => handleClick(step)}
                        // Volle Variante: Label-Wort UND Kurztext sind
                        // sichtbar (`includeLabel: true`, siehe `accessibleName`).
                        aria-label={accessibleName(step, true)}
                        className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <span className={`h-4 w-4 shrink-0 rounded-full grid place-items-center ${MARK_CLASS[step.state]}`}>
                          <StepMark state={step.state} />
                        </span>
                        <span className={`font-bold ${LABEL_CLASS[step.state]}`}>
                          {t(`dashboard:journey.${step.id}.label`)}
                        </span>
                        <span className="tabular-nums text-muted-foreground">{shortText(step)}</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{tooltipText(step)}</TooltipContent>
                  </Tooltip>
                </li>
                {index < steps.length - 1 && (
                  <li aria-hidden="true" className="text-muted-foreground text-xs px-0.5 select-none">
                    ·
                  </li>
                )}
              </Fragment>
            );
          })}
        </ol>
      </nav>
    </TooltipProvider>
  );
}
